import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { ApiPageResponse } from '../types/api.types';
import {
  AdminCreateOperatorRequest,
  AdminUpdateOperatorRequest,
  ApproveOperatorRequest,
  CreditReferenceRequest,
  EffectiveRatesResponse,
  Operator,
  OperatorView,
} from '../types/operator.types';

@Injectable({ providedIn: 'root' })
export class OperatorsService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.gatewayUrl}/operator/api`;

  /** Paginated admin list — `GET /operators?page&size&zoneCode`. */
  list(opts: { page?: number; size?: number; zoneCode?: string } = {}): Observable<ApiPageResponse<OperatorView>> {
    let params = new HttpParams()
      .set('page', String(opts.page ?? 1))
      .set('size', String(opts.size ?? 20));
    if (opts.zoneCode?.trim()) params = params.set('zoneCode', opts.zoneCode.trim());
    return this.http.get<ApiPageResponse<OperatorView>>(
      `${this.base}/operators`,
      { params },
    );
  }

  /** Single operator (full entity) — `GET /operators/{id}`. */
  get(id: number): Observable<Operator> {
    return this.http.get<Operator>(`${this.base}/operators/${id}`);
  }

  /** Approve a PENDING operator. Body is optional. */
  approve(id: number, body?: ApproveOperatorRequest): Observable<OperatorView> {
    return this.http.post<OperatorView>(
      `${this.base}/operators/${id}/approve`,
      body ?? {},
    );
  }

  /** Update or clear the credit reference override (admin). */
  setCreditReference(id: number, body: CreditReferenceRequest): Observable<OperatorView> {
    return this.http.put<OperatorView>(
      `${this.base}/admin/operators/${id}/credit-reference`,
      body,
    );
  }

  /** Admin creates an operator for a given userId — `POST /admin/operators`. */
  create(body: AdminCreateOperatorRequest): Observable<OperatorView> {
    return this.http.post<OperatorView>(`${this.base}/admin/operators`, body);
  }

  /** Partial edit of business fields — `PATCH /admin/operators/{id}`. */
  update(id: number, body: AdminUpdateOperatorRequest): Observable<OperatorView> {
    return this.http.patch<OperatorView>(`${this.base}/admin/operators/${id}`, body);
  }

  /** Effective commission rates for this operator (override merged with global).
   *  `usesCustomRates` is true if the override is active, false if globals apply. */
  // ── Dossiers KYC opérateur (#66) ────────────────────────────────────────

  /**
   * File d'attente des dossiers KYC opérateur à valider (statut `UNDER_REVIEW`) —
   * `GET /admin/operators/kyc/pending`. Paginé 1-indexé + enveloppe Nawam,
   * trié par date de soumission croissante. Garde `ROLE_ADMIN`/`operator_manage`.
   */
  kycPending(page = 1, size = 20): Observable<ApiPageResponse<OperatorView>> {
    const params = new HttpParams().set('page', String(page)).set('size', String(size));
    return this.http.get<ApiPageResponse<OperatorView>>(
      `${this.base}/admin/operators/kyc/pending`,
      { params },
    );
  }

  /** Valide le dossier KYC → `VALIDATED` (débloque son recrutement) + notif opérateur. */
  approveKyc(operatorId: number): Observable<OperatorView> {
    return this.http.post<OperatorView>(
      `${this.base}/admin/operators/${operatorId}/kyc/approve`,
      {},
    );
  }

  /** Rejette le dossier KYC → `REJECTED` (motif obligatoire) + notif opérateur. */
  rejectKyc(operatorId: number, reason: string): Observable<OperatorView> {
    return this.http.post<OperatorView>(
      `${this.base}/admin/operators/${operatorId}/kyc/reject`,
      { reason },
    );
  }

  effectiveRates(id: number): Observable<EffectiveRatesResponse> {
    return this.http.get<EffectiveRatesResponse>(
      `${this.base}/operators/${id}/effective-rates`,
    );
  }
}
